const FEATURED_CHALLENGE_PRIORITY = [
  // Change this order when the seasonal featured challenge changes.
  {
    key: "the-big-canada-run",
    match: ["the big canada run", "big canada run"],
  },
];

function normalizeChallengeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getRaceValues(userRaces) {
  if (!userRaces) return [];
  if (userRaces instanceof Map) return Array.from(userRaces.values());
  if (Array.isArray(userRaces)) return userRaces;
  return Object.values(userRaces);
}

function getRaceTime(race, field) {
  const rawValue = race?.raceInfo?.[field];
  if (!rawValue) return null;

  const time = new Date(rawValue).getTime();
  return Number.isFinite(time) ? time : null;
}

function getRaceStartTime(race) {
  return getRaceTime(race, "raceDateStart") || 0;
}

function getRaceEndTime(race) {
  return getRaceTime(race, "raceDateEnd") || 0;
}

function getLocalTodayKey(nowMs, timeZone) {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat("en-CA", {
      ...options,
      ...(timeZone ? {timeZone} : {}),
    }).format(new Date(nowMs));
  } catch (_error) {
    return new Intl.DateTimeFormat("en-CA", options).format(new Date(nowMs));
  }
}

function getActivityDateWindow(race) {
  if (race?.raceInfo?.activityDatePolicy !== "participant_local_v1") {
    return null;
  }
  const start = race?.raceInfo?.activityStartDate;
  const end = race?.raceInfo?.activityEndDate;
  const isDayKey = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (!isDayKey(start) || !isDayKey(end) || start > end) return null;
  return {start, end};
}

function getChallengePriority(race) {
  const searchText = normalizeChallengeText(
    [
      race?.raceInfo?.name,
      race?.raceInfo?.legName,
      race?.raceId,
      race?.legId,
    ].filter(Boolean).join(" ")
  );

  const priorityIndex = FEATURED_CHALLENGE_PRIORITY.findIndex((challenge) =>
    challenge.match.some((matcher) => searchText.includes(normalizeChallengeText(matcher)))
  );

  return priorityIndex === -1 ? FEATURED_CHALLENGE_PRIORITY.length : priorityIndex;
}

function sortFeaturedChallenges(a, b) {
  const priorityDiff = getChallengePriority(a) - getChallengePriority(b);
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  const startDiff = getRaceStartTime(b) - getRaceStartTime(a);
  if (startDiff !== 0) {
    return startDiff;
  }

  const nameDiff = (a?.raceInfo?.name || "").localeCompare(b?.raceInfo?.name || "");
  if (nameDiff !== 0) {
    return nameDiff;
  }

  return (a?.raceInfo?.legName || "").localeCompare(b?.raceInfo?.legName || "");
}

function hasValidChallengeWindow(race) {
  return getRaceTime(race, "raceDateStart") !== null && getRaceTime(race, "raceDateEnd") !== null;
}

export function isRunningChallenge(race, nowMs = Date.now(), timeZone = "") {
  if (race?.completedDate) return false;

  const activityDateWindow = getActivityDateWindow(race);
  if (activityDateWindow) {
    const today = getLocalTodayKey(nowMs, timeZone);
    return activityDateWindow.start <= today && today <= activityDateWindow.end;
  }

  const startTime = getRaceTime(race, "raceDateStart");
  const endTime = getRaceTime(race, "raceDateEnd");

  if (!hasValidChallengeWindow(race)) {
    return false;
  }

  return startTime <= nowMs && endTime >= nowMs;
}

export function isUpcomingChallenge(race, nowMs = Date.now(), timeZone = "") {
  if (race?.completedDate) return false;

  const activityDateWindow = getActivityDateWindow(race);
  if (activityDateWindow) {
    return getLocalTodayKey(nowMs, timeZone) < activityDateWindow.start;
  }

  const startTime = getRaceTime(race, "raceDateStart");

  if (!hasValidChallengeWindow(race)) {
    return false;
  }

  return startTime > nowMs && getRaceEndTime(race) >= startTime;
}

export function getFeaturedChallenge(userRaces, nowMs = Date.now(), timeZone = "") {
  const races = getRaceValues(userRaces);
  const runningChallenge = races
    .filter((race) => isRunningChallenge(race, nowMs, timeZone))
    .sort(sortFeaturedChallenges)[0];

  if (runningChallenge) {
    return runningChallenge;
  }

  return races
    .filter((race) => isUpcomingChallenge(race, nowMs, timeZone))
    .sort(sortFeaturedChallenges)[0] || null;
}

export function getChallengeMenuLabel(race) {
  const raceName = String(race?.raceInfo?.name || "").trim();
  const normalizedName = normalizeChallengeText(raceName);

  if (normalizedName.includes("big canada run")) {
    return "Canada Run";
  }

  return raceName.replace(/^the\s+/i, "").replace(/\s+challenge$/i, "").trim() || "Challenge";
}
